/**
 * Live PiP transcript → thread messages (#1307, CP-12/C-6).
 *
 * The thread is the record (§15.6 rule 4): speaker-labeled voice bubbles
 * stream into the SAME MessageList the text conversation renders. This
 * mapper is pure — the page merges its output with the fetched/streamed
 * chat messages and MessageList’s render-time chronological re-sort absorbs
 * ordering races.
 *
 * Timestamps are synthesized as baseTime + arrival index (milliseconds):
 * stable across re-renders (an utterance updates in place under its id, so
 * its index never moves) and strictly ordered, which is all the re-sort
 * needs. The durable timestamps live server-side with the writer of record
 * (arch Â§5.1); these exist only for the live view’s ordering.
 */
import type { Message } from '../../chat/types'
import type { PriorCaption } from '../captionHydration'
import type { TranscriptSegment } from './types'

/** Marks a live (not yet server-persisted) voice bubble; ids are namespaced
 * so they can never collide with server message ids on merge. */
export const LIVE_VOICE_ID_PREFIX = 'live-voice-'

/**
 * Fold one arriving segment into the accumulated transcript (#1332).
 *
 * Identity is `segment.id`, which livekitRoom sets from `lk.segment_id` —
 * one utterance, not one stream. LiveKit reads a segment’s interim and final
 * streams in two concurrent handlers, so a slow interim can close after the
 * final; a settled utterance therefore never accepts a non-final update.
 */
export function mergeSegment(
  prev: readonly TranscriptSegment[],
  segment: TranscriptSegment,
): TranscriptSegment[] {
  const idx = prev.findIndex((s) => s.id === segment.id)
  if (idx === -1) return [...prev, segment]
  if (prev[idx].final && !segment.final) return prev as TranscriptSegment[]
  const next = prev.slice()
  next[idx] = segment
  return next
}

export function transcriptToMessages(
  segments: TranscriptSegment[],
  baseTime: number,
): Message[] {
  return segments
    .filter((s) => s.text.trim().length > 0)
    .map((s, index) => ({
      id: `${LIVE_VOICE_ID_PREFIX}${s.id}`,
      content: s.text,
      sender: (s.speaker === 'user' ? 'user' : 'stevens') as Message['sender'],
      timestamp: new Date(baseTime + index),
      isStreaming: !s.final,
      // TRF delta 2026-09-07: a live PiP turn IS a voice turn. The persisted
      // row gets `source` from MessageV5.metadata on the next history load;
      // stamping it here means the speaker mark appears while the call is
      // still running instead of only after a reload.
      metadata: { source: 'voice' },
    }))
}

/**
 * C-21 refresh half: prior captions (hydrated from the durable record on
 * every (re)mount) rendered ahead of the live segments. Dedup against live
 * text is the caller’s concern only when re-attaching to a session that is
 * still running — pass the live texts and matching hydrated rows drop.
 */
export function priorCaptionsToMessages(
  captions: PriorCaption[],
  baseTime: number,
  liveTexts?: ReadonlyArray<string>,
): Message[] {
  const liveSet = new Set((liveTexts ?? []).map((t) => t.trim()))
  return captions
    .filter((c) => c.text.trim().length > 0 && !liveSet.has(c.text.trim()))
    .map((c, index) => ({
      id: `${LIVE_VOICE_ID_PREFIX}prior-${c.id}`,
      content: c.text,
      sender: (c.isUser ? 'user' : 'stevens') as Message['sender'],
      // Prior turns sit strictly before the live window.
      timestamp: new Date(baseTime - captions.length + index),
      isStreaming: false,
      metadata: { source: 'voice' },
    }))
}
